// Gate de etapa: al mover una tarjeta a una etapa con campos obligatorios
// sin completar, pide esos campos en pantalla antes de confirmar el movimiento.
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../App";
import { CustomFieldInput } from "./CardDialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StageGateDialog({ card, stage, missing, requireComment = false, commentPrompts = [], onClose, onDone }) {
  const { getAuthHeaders } = useAuth();
  const [values, setValues] = useState({});
  const [ecd, setEcd] = useState("");
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const coreMissing = missing.filter((m) => m.core);
  const customMissing = missing.filter((m) => !m.core);

  const handleSubmit = async () => {
    const faltantes = [];
    if (coreMissing.length && !ecd) faltantes.push("Fecha de cierre esperada");
    for (const m of customMissing) {
      const v = values[m.name];
      if (v === undefined || v === null || v === "" || v === false || (Array.isArray(v) && v.length === 0)) faltantes.push(m.label);
    }
    if (faltantes.length) { toast.error(`Debe completar: ${faltantes.join(", ")}`); return; }
    if (requireComment && !comment.trim()) { toast.error("Debe escribir el comentario/justificación obligatorio"); return; }
    let lost_reason = "";
    if (stage?.is_lost) lost_reason = window.prompt("Motivo del cierre perdido (opcional):") || "";
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/pipelines/cards/${card.id}/move`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          stage_id: stage.id,
          lost_reason,
          field_updates: Object.keys(values).length ? values : null,
          expected_close_date: ecd || null,
          transition_comment: comment.trim() || "",
        }),
      });
      if (!r.ok) {
        const e = await r.json();
        const d = e.detail;
        throw new Error(typeof d === "string" ? d : (d?.message || "Error al mover la tarjeta"));
      }
      toast.success(`Tarjeta movida a "${stage.name}"`);
      onDone?.();
    } catch (err) { toast.error(err.message || "Error al mover la tarjeta"); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="stage-gate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" /> Requisitos de la etapa "{stage.name}"
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          Para mover <b>{card.title}</b> a <b>{stage.name}</b> debés completar los siguientes requisitos:
        </p>
        <div className="space-y-3">
          {coreMissing.length > 0 && (
            <div>
              <Label className="text-xs">Fecha de cierre esperada <span className="text-rose-500">*</span></Label>
              <Input type="date" value={ecd} onChange={(e) => setEcd(e.target.value)} data-testid="stage-gate-input-expected_close_date" />
            </div>
          )}
          {customMissing.map((m) => (
            <CustomFieldInput
              key={m.name}
              field={{ ...m.field, required: true }}
              value={values[m.name]}
              onChange={(v) => setValues((p) => ({ ...p, [m.name]: v }))}
              testIdPrefix="stage-gate-input-"
            />
          ))}
          {requireComment && (
            <div>
              <Label className="text-xs">
                {commentPrompts[0] || "Comentario / justificación"} <span className="text-rose-500">*</span>
              </Label>
              {commentPrompts.slice(1).map((p, i) => (
                <p key={i} className="text-[11px] text-slate-500">{p}</p>
              ))}
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                placeholder="Escriba el comentario obligatorio…"
                data-testid="stage-gate-comment"
              />
              <p className="text-[10px] text-slate-400 mt-1">Se guardará como nota de la tarjeta.</p>
            </div>
          )}
        </div>
        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={onClose} data-testid="stage-gate-cancel-btn">Cancelar</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-[#80BC00] hover:bg-[#6ea700] text-white" data-testid="stage-gate-submit-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Completar y mover
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
